// src/pages/ResumoReadPage.tsx
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { resumoService } from '../services/resumo.service';
import { ResumoData } from '../components/resumo/types';
import PageNavigator, { ResumoPage } from '../components/resumo/components/PageNavigator';
import Toast from '../components/Toast';
import AutoHideHeader from '../components/resumo/layout/AutoHideHeader';
import CompactPageNav from '../components/resumo/layout/CompactPageNav';
import AutoHideFooter from '../components/resumo/layout/AutoHideFooter';
import AnnotationLayer from '../components/resumo/annotations/AnnotationLayer';
import { useAnnotations } from '../components/resumo/annotations/useAnnotations';
import { ReaderMode } from '../components/resumo/annotations/annotations.types';
import '../components/resumo/annotations/annotations.css';
import '../components/resumo/annotations/UnifiedAnnotationToolbar.css';
import { Edit, Eye, ArrowLeft, RefreshCw } from 'lucide-react';
import HighlightedContent from '../components/resumo/annotations/HighlightedContent';
import '../components/resumo/annotations/NotesSidebar.css';
import '../components/resumo/annotations/NoteModal.css';
import '../components/resumo/annotations/NoteQuizModal.css';
import '../components/resumo/annotations/HighlightCommentIcon.css';
import { useAntiPiracy } from '../hooks/useAntiPiracy';
import UnsavedChangesModal from '../components/resumo/annotations/UnsavedChangesModal';
import { useLayoutEffect } from 'react';
import { QuizReadManager, splitContentByQuiz, loadQuizProgress, saveQuizProgress } from '../components/resumo/quiz/QuizReadManager';
import QuizInlineAccordion from '../components/resumo/quiz/QuizInlineAccordion';
import { QuizProgress, DEFAULT_QUIZ_PROGRESS } from '../components/resumo/quiz/quiz.types';
import '../components/resumo/quiz/QuizCard.css';
import '../components/resumo/quiz/QuizInlineAccordion.css';
import { ImageZoomModal } from '../components/CardEditor/ImageZoomModal';
import '../components/resumo/TableManager.css';
import { useResumoOnboarding } from '../hooks/useResumoOnboarding';
import { ResumoOnboarding } from '../components/resumo/onboarding/ResumoOnboarding';
import PageLimitModal from '../components/resumo/PageLimitModal';
import RyanCompraModal from '../ryan/components/RyanCompraModal';
import type { ApostilaPreviewData } from '../services/apostilaPreview.service';

import {
    ChevronLeft,
    Maximize2,
    Minimize2,
    Edit3,
    BookOpen,
    Upload
} from 'lucide-react';
import './ResumoReadPage.css';
import '../components/resumo/FlashcardBadge.css';

interface ResumoReadPageProps {
    isCustomDeck?: boolean;
    // Presença = modo prévia pública (rota /previa/:urlSlug, via
    // ApostilaPreviewPage.tsx, sem autenticação). O wrapper já buscou e
    // validou o link (expiração etc.) antes de montar este componente — aqui
    // só consumimos os dados já prontos, sem chamar apostilaPreviewService
    // de novo.
    previewData?: ApostilaPreviewData;
}

/** Constrói um player de áudio GRAIA usando DOM puro + addEventListener.
 *  Não usa React/createRoot para evitar conflito com o event delegation do root principal.
 */
function buildDOMaudioPlayer(src: string): HTMLElement {
    const audio = document.createElement('audio');
    audio.src = src;
    audio.preload = 'metadata';
    audio.style.display = 'none';

    const FILL = getComputedStyle(document.body).getPropertyValue('--platform-primary').trim() || '#003BD1';
    const TRACK = getComputedStyle(document.body).getPropertyValue('--platform-a12').trim() || 'rgba(0,49,83,0.12)';

    const formatTime = (t: number) => {
        if (!isFinite(t)) return '0:00';
        const m = Math.floor(t / 60);
        const s = Math.floor(t % 60);
        return `${m}:${s.toString().padStart(2, '0')}`;
    };

    // ---- elementos ----
    const player = document.createElement('div');
    player.className = 'graia-audio-player';
    player.style.cssText = 'width:100%;max-width:500px;pointer-events:auto;';

    const playBtn = document.createElement('button');
    playBtn.type = 'button';
    playBtn.className = 'graia-audio-play-btn';
    playBtn.setAttribute('aria-label', 'Reproduzir');
    const iconPlay = `<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="transform:translateX(1.5px)"><polygon points="5 3 19 12 5 21 5 3"/></svg>`;
    const iconPause = `<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="6" y="4" width="4" height="16"/><rect x="14" y="4" width="4" height="16"/></svg>`;
    playBtn.innerHTML = iconPlay;

    const progressContainer = document.createElement('div');
    progressContainer.className = 'graia-audio-progress-container';

    const progressBar = document.createElement('input');
    progressBar.type = 'range';
    progressBar.className = 'graia-audio-progress-bar';
    progressBar.min = '0';
    progressBar.max = '0';
    progressBar.value = '0';
    progressBar.step = '0.01';
    progressBar.style.background = TRACK;

    const timeDiv = document.createElement('div');
    timeDiv.className = 'graia-audio-time';
    const timeLeft = document.createElement('span');
    timeLeft.textContent = '0:00';
    const timeRight = document.createElement('span');
    timeRight.textContent = '0:00';
    timeDiv.appendChild(timeLeft);
    timeDiv.appendChild(timeRight);

    const volumeContainer = document.createElement('div');
    volumeContainer.className = 'graia-audio-volume-container';

    const muteBtn = document.createElement('button');
    muteBtn.type = 'button';
    muteBtn.className = 'graia-audio-volume-btn';
    muteBtn.setAttribute('aria-label', 'Silenciar');
    const iconVol = `<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/><path d="M19.07 4.93a10 10 0 0 1 0 14.14"/><path d="M15.54 8.46a5 5 0 0 1 0 7.07"/></svg>`;
    const iconMute = `<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/><line x1="23" y1="9" x2="17" y2="15"/><line x1="17" y1="9" x2="23" y2="15"/></svg>`;
    muteBtn.innerHTML = iconVol;

    const volSlider = document.createElement('input');
    volSlider.type = 'range';
    volSlider.className = 'graia-audio-volume-slider';
    volSlider.min = '0';
    volSlider.max = '1';
    volSlider.step = '0.01';
    volSlider.value = '1';
    volSlider.style.background = `linear-gradient(to right, ${FILL} 0%, ${FILL} 100%, ${TRACK} 100%, ${TRACK} 100%)`;

    // ---- montagem ----
    progressContainer.appendChild(progressBar);
    progressContainer.appendChild(timeDiv);
    volumeContainer.appendChild(muteBtn);
    volumeContainer.appendChild(volSlider);
    player.appendChild(audio);
    player.appendChild(playBtn);
    player.appendChild(progressContainer);
    player.appendChild(volumeContainer);

    // ---- lógica ----
    const updateProgress = () => {
        const pct = audio.duration > 0 ? (audio.currentTime / audio.duration) * 100 : 0;
        progressBar.style.background = `linear-gradient(to right, ${FILL} 0%, ${FILL} ${pct}%, ${TRACK} ${pct}%, ${TRACK} 100%)`;
        progressBar.value = String(audio.currentTime);
        timeLeft.textContent = formatTime(audio.currentTime);
    };

    audio.addEventListener('loadedmetadata', () => {
        progressBar.max = String(audio.duration);
        timeRight.textContent = formatTime(audio.duration);
    });

    audio.addEventListener('timeupdate', updateProgress);

    audio.addEventListener('ended', () => {
        playBtn.innerHTML = iconPlay;
        playBtn.setAttribute('aria-label', 'Reproduzir');
    });

    playBtn.addEventListener('click', () => {
        if (audio.paused) {
            audio.play().catch(() => {});
            playBtn.innerHTML = iconPause;
            playBtn.setAttribute('aria-label', 'Pausar');
        } else {
            audio.pause();
            playBtn.innerHTML = iconPlay;
            playBtn.setAttribute('aria-label', 'Reproduzir');
        }
    });

    progressBar.addEventListener('input', () => {
        audio.currentTime = parseFloat(progressBar.value);
        updateProgress();
    });

    muteBtn.addEventListener('click', () => {
        audio.muted = !audio.muted;
        muteBtn.innerHTML = audio.muted ? iconMute : iconVol;
    });

    volSlider.addEventListener('input', () => {
        const v = parseFloat(volSlider.value);
        audio.volume = v;
        audio.muted = v === 0;
        muteBtn.innerHTML = v === 0 ? iconMute : iconVol;
        const pct = v * 100;
        volSlider.style.background = `linear-gradient(to right, ${FILL} 0%, ${FILL} ${pct}%, ${TRACK} ${pct}%, ${TRACK} 100%)`;
    });

    return player;
}

const ResumoReadPage: React.FC<ResumoReadPageProps> = ({ isCustomDeck = false, previewData }) => {
    const { deckId, subdeckId } = useParams<{ deckId: string; subdeckId: string }>();
    const navigate = useNavigate();
    const { isAdmin, user, isVip, isApostilaRevisor, loading: authLoading } = useAuth();

    // Modo prévia é definido pela PRESENÇA do prop, nunca por estado de auth
    // — mesmo um admin logado abrindo um link de prévia deve ver a
    // experiência de demonstração (sem persistir nada, sem botões de
    // editor), não a leitura real por acaso ter uma conta autenticada.
    const isPreview = !!previewData;

    const location = useLocation();
    const isRyanEditor = (location.state as any)?.isRyanEditor === true;
    // Chute otimista pra evitar flash de "bloqueado" antes da resposta do
    // backend chegar — location.state pode estar ausente (refresh, nova aba,
    // link direto) ou desatualizado, então loadResumo() abaixo corrige os
    // dois nos dois sentidos com o valor autoritativo do servidor assim que
    // a apostila carrega (fonte de verdade real: GET /resumo, que agora já
    // aplica o corte de página no próprio conteúdo retornado).
    const [isPurchasedDeck, setIsPurchasedDeck] = useState<boolean>(
        isAdmin || isVip || ((location.state as any)?.isPurchasedDeck === true)
    );
    const [visiblePagesCount, setVisiblePagesCount] = useState<number>(() => {
        if (isAdmin || isVip || ((location.state as any)?.isPurchasedDeck === true)) return Infinity;
        const limit = (location.state as any)?.freeApostilaPagesLimit || 0;
        return limit > 0 ? limit : 0;
    });

    const { theme } = useTheme();
    const darkMode = theme === 'dark';
    const onboarding = useResumoOnboarding();

    const [resumo, setResumo] = useState<ResumoData | null>(null);
    const [pages, setPages] = useState<ResumoPage[]>([]);
    const [currentPageIndex, setCurrentPageIndex] = useState(0);
    const [loading, setLoading] = useState(true);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [readProgress, setReadProgress] = useState(0);
    const [, setHasRead] = useState(false);
    // Progresso de leitura por página (compartilhado GRAIA/Ryan): a página 1 e
    // a última contam como lidas só de entrar nelas; as do meio exigem 90s de
    // permanência. Simples ref (não precisa re-renderizar nada aqui — quem
    // exibe o % de leitura é o card do subdeck, que lê do backend à parte).
    const pagesReadRef = useRef<Set<number>>(new Set());
    const [cards, setCards] = useState<any[]>([]);
    const cardsRef = useRef<any[]>([]);
    const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' | 'info' } | null>(null);
    const scrollRef = React.useRef<HTMLDivElement>(null);

    const [quizReadManager] = useState(() => new QuizReadManager());
    // Progresso de cada quiz (respostas, nota, questão atual) — persiste entre
    // navegação de página e reload via localStorage, hidratado sob demanda.
    const [quizProgress, setQuizProgress] = useState<Record<string, QuizProgress>>({});
    const getQuizProgress = useCallback((quizId: string): QuizProgress => {
        if (quizProgress[quizId]) return quizProgress[quizId];
        return loadQuizProgress(quizId) || DEFAULT_QUIZ_PROGRESS;
    }, [quizProgress]);
    const handleQuizProgressChange = useCallback((quizId: string, progress: QuizProgress) => {
        setQuizProgress(prev => ({ ...prev, [quizId]: progress }));
        saveQuizProgress(quizId, progress);
    }, []);

    const [zoomedImage, setZoomedImage] = useState<string | null>(null);
    const [zoomScale, setZoomScale] = useState(1);

    useLayoutEffect(() => {
        const container = scrollRef.current;
        if (container) {
            container.scrollTop = 0;
        }
        window.scrollTo(0, 0);
    }, [currentPageIndex]);

    const [mode, setMode] = useState<ReaderMode>('read');

    const headerRef = React.useRef<HTMLDivElement>(null);
    const toolbarRef = React.useRef<HTMLDivElement>(null);

    const [isDirty, setIsDirty] = useState(false);
    const isDirtyRef = useRef(false); // ref síncrona para evitar closure stale no goBack
    const [showUnsavedModal, setShowUnsavedModal] = useState(false);
    const [pendingAction, setPendingAction] = useState<(() => void) | null>(null);
    const [showPageLimitModal, setShowPageLimitModal] = useState(false);

    // Manter isDirtyRef sincronizado
    useEffect(() => { isDirtyRef.current = isDirty; }, [isDirty]);

    // Manter cardsRef sincronizado
    useEffect(() => {
        cardsRef.current = cards;
    }, [cards]);

    useAntiPiracy(!isAdmin);

    const {
        annotations,
        addHighlight,
        removeHighlight,
        bulkUpdateHighlights,
        addNote,
        updateNoteComplete,
        updateHighlightNote,
        removeNote,
        addComment,
        removeComment,
        updateComment,
        saveAnnotations,
        undo: annotationUndo,
        redo: annotationRedo,
        canUndo: annotationCanUndo,
        canRedo: annotationCanRedo,
        getAutosaveInfo,
        clearAutosave,
        loadAnnotations: reloadAnnotations,
    } = useAnnotations(
        // Prévia: sempre string vazia, mesmo se houver um usuário real logado
        // (ex: admin abrindo o próprio link de prévia) — autosaveKey vira
        // null e o efeito de carregar anotações (que exige os 3 truthy) nunca
        // dispara, então não existe NENHUMA chamada de rede de anotação
        // partindo daqui em modo prévia.
        isPreview ? '' : (user?.userId || ''),
        isPreview ? '' : (deckId || ''),
        isPreview ? '' : (subdeckId || '')
    );

    // ====== MODO PRÉVIA — anotações são só demonstração ======
    // Grifar/comentar/anotar precisam aparecer na toolbar (pra mostrar que o
    // recurso existe), mas nenhuma ação deve alterar dado nenhum — troca os
    // callbacks passados pro AnnotationLayer por estes em vez dos reais
    // (addHighlight/addNote/etc.), então annotations.* do hook acima nunca
    // é mutado em modo prévia.
    const handlePreviewAnnotationBlocked = useCallback(() => {
        setToast({ message: 'Crie uma conta gratuita para grifar, comentar e anotar.', type: 'info' });
    }, []);

    const handlePreviewSave = useCallback(async () => {
        setToast({ message: 'Crie uma conta gratuita para salvar suas anotações.', type: 'info' });
        return { success: false, message: 'Prévia: anotações não são salvas.' };
    }, []);

    // CTA de "assinar" dentro da prévia — vai direto pro checkout do produto
    // que vende este conteúdo (mesmo destino do "Quero adquirir agora" em
    // RyanProductPage.tsx) quando o backend resolveu um checkoutSlug; sem
    // produto público associado, cai no fallback genérico de sempre.
    const goToPreviewCheckout = useCallback(() => {
        if (previewData?.checkoutSlug) {
            navigate(`/ryan/checkout/${previewData.checkoutSlug}`);
        } else {
            navigate('/login');
        }
    }, [previewData, navigate]);

    // ====== RECUPERAÇÃO DE AUTOSAVE ======
    const [showAutosaveRecovery, setShowAutosaveRecovery] = useState(false);
    const [autosaveRecoveryTime, setAutosaveRecoveryTime] = useState<string | null>(null);
    const autosaveChecked = useRef(false);

    useEffect(() => {
        // Verificar autosave apenas ao entrar no modo Interagir, e só uma vez
        if (mode !== 'annotate') return;
        if (autosaveChecked.current) return;
        autosaveChecked.current = true;

        const info = getAutosaveInfo();
        if (!info) return;

        const savedAt = new Date(info.savedAt);
        const now = new Date();
        const ageMinutes = (now.getTime() - savedAt.getTime()) / 60000;

        // Mostrar recuperação apenas se o autosave tiver menos de 24h
        if (ageMinutes < 1440) {
            const fmt = savedAt.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
            setAutosaveRecoveryTime(fmt);
            setShowAutosaveRecovery(true);
        }
    }, [mode, getAutosaveInfo]);

    const contentAreaRef = React.useRef<HTMLElement | null>(null);

    const loadResumo = useCallback(async () => {
        // Modo prévia: dados já vieram prontos do wrapper (ApostilaPreviewPage
        // já buscou e validou o link) — nunca chama resumoService (autenticado)
        // nem apostilaPreviewService de novo aqui, só normaliza pro mesmo
        // shape de estado usado pelo resto do componente.
        if (isPreview && previewData) {
            setResumo({
                title: previewData.title,
                blocks: [],
                metadata: {
                    subdeckId: previewData.subdeckId,
                    subdeckName: previewData.subdeckName,
                    deckId: previewData.deckId,
                    deckName: previewData.deckName,
                    lastEdited: '',
                    version: 1,
                },
                isPublished: true,
                quizzes: previewData.quizzes as any,
            });
            setIsPurchasedDeck(false);
            setVisiblePagesCount(previewData.previewPagesLimit ?? Infinity);
            if (previewData.quizzes) {
                quizReadManager.importQuizzes(previewData.quizzes);
            }
            setPages(previewData.pages || []);
            setLoading(false);
            return;
        }

        if (!deckId || !subdeckId) return;

        try {
            setLoading(true);

            const [data, cardsData] = await Promise.all([
                resumoService.getResumo(deckId, subdeckId, isCustomDeck),
                resumoService.getSubdeckCards(deckId, subdeckId, isCustomDeck).catch(() => [])
            ]);

            setCards(cardsData);
            setResumo(data.resumo);

            // Backend agora é a fonte de verdade (calcula isPurchased/visiblePagesCount
            // por plataforma e já manda o content das páginas bloqueadas vazio) —
            // corrige o chute otimista nos dois sentidos, não só de false→true.
            // Isso também é o que resolve o caso de location.state ausente (ex:
            // apostila aberta via link em nova aba, ou refresh da página).
            if (typeof data.isPurchased === 'boolean') {
                setIsPurchasedDeck(data.isPurchased);
            }
            if (typeof data.visiblePagesCount === 'number') {
                setVisiblePagesCount(data.visiblePagesCount);
            }

            if (data.resumo.isBlocked && !isAdmin) {
                setResumo(null);
                setPages([]);
                setLoading(false);
                return;
            }

            setHasRead(data.hasRead || false);

            if (!isCustomDeck) {
                resumoService.getReadProgress(deckId, subdeckId)
                    .then(progress => { pagesReadRef.current = new Set(progress.pagesRead); })
                    .catch(() => {});
            }

            if (data.resumo.quizzes) {
                quizReadManager.importQuizzes(data.resumo.quizzes);
                console.log('✅ Quizzes carregados:', Object.keys(data.resumo.quizzes).length);
            }

            const loadedPages = data.resumo.pages || [];

            if (loadedPages.length > 0) {
                setPages(loadedPages);
                console.log('✅ Páginas carregadas:', loadedPages.length);
            } else if (data.resumo.blocks && data.resumo.blocks.length > 0) {
                const singlePage: ResumoPage = {
                    id: 'page-1',
                    title: data.resumo.title || 'Página 1',
                    content: data.resumo.blocks[0]?.content as string || '',
                    order: 0
                };
                setPages([singlePage]);
                console.log('✅ Página única criada a partir de blocks');
            } else {
                setPages([]);
                console.warn('⚠️ Nenhum conteúdo encontrado');
            }

        } catch (error: any) {
            console.error('❌ Erro ao carregar resumo:', error);
            setToast({ message: 'Erro ao carregar resumo', type: 'error' });
        } finally {
            setLoading(false);
        }
    }, [isPreview, previewData, deckId, subdeckId, isCustomDeck, quizReadManager, isAdmin]);

    // Espera a sessão ser verificada — num recarregamento completo isAdmin começa
    // false, e o admin via por um instante a apostila interditada como bloqueada
    useEffect(() => {
        if (!authLoading) loadResumo();
    }, [loadResumo, authLoading]);

    // Registra uma página como lida (idempotente — pagesReadRef evita reenviar
    // uma página já confirmada). Se o POST falhar, desfaz a marca local pra
    // permitir tentar de novo numa próxima visita à página. isPreview é
    // redundante com !deckId (a rota de prévia não tem esses params), mas
    // deixado explícito de propósito — não deve depender de coincidência.
    const markPageAsRead = useCallback((pageIndex: number) => {
        if (!deckId || !subdeckId || isCustomDeck || isPreview || pagesReadRef.current.has(pageIndex)) return;
        pagesReadRef.current.add(pageIndex);
        resumoService.markPageRead(deckId, subdeckId, pageIndex).catch(() => {
            pagesReadRef.current.delete(pageIndex);
        });
    }, [deckId, subdeckId, isCustomDeck, isPreview]);

    // Progresso de leitura real (GRAIA + Ryan): primeira e última página contam
    // como lidas assim que abertas; as do meio precisam de 90s de permanência
    // pra contar (cancelado se o usuário sair da página antes disso).
    useEffect(() => {
        if (isCustomDeck || isPreview || loading || pages.length === 0) return;
        if (pagesReadRef.current.has(currentPageIndex)) return;

        const isFirstOrLast = currentPageIndex === 0 || currentPageIndex === pages.length - 1;
        if (isFirstOrLast) {
            markPageAsRead(currentPageIndex);
            return;
        }

        const timer = setTimeout(() => markPageAsRead(currentPageIndex), 90000);
        return () => clearTimeout(timer);
    }, [currentPageIndex, pages.length, loading, isCustomDeck, isPreview, markPageAsRead]);


    // Listener delegado único para cliques nos botões de gabarito (sem closures sobre elementos DOM)
    useEffect(() => {
        const handleGabaritoClick = (e: MouseEvent) => {
            const target = e.target as HTMLElement;
            const btn = target.closest('.gabarito-reveal-btn, .gabarito-hide-btn') as HTMLElement | null;
            if (!btn) return;

            e.stopPropagation();
            e.preventDefault();

            const callout = btn.closest<HTMLElement>('.resumo-callout');
            if (!callout) return;

            const isReveal = btn.classList.contains('gabarito-reveal-btn');
            callout.dataset.gabaritoRevealed = String(isReveal);

            // Atualizar blur do conteúdo
            callout.querySelectorAll<HTMLElement>('.callout-title, .callout-body').forEach(ce => {
                ce.style.filter = isReveal ? '' : 'blur(7px)';
                ce.style.userSelect = isReveal ? '' : 'none';
                ce.style.pointerEvents = isReveal ? '' : 'none';
            });

            // Remover/adicionar botão de revelar
            if (isReveal) {
                callout.querySelectorAll('.gabarito-reveal-btn').forEach(b => b.remove());
            } else {
                const color = callout.dataset.calloutColor || getComputedStyle(document.body).getPropertyValue('--platform-accent').trim();
                const newBtn = document.createElement('button');
                newBtn.className = 'gabarito-reveal-btn';
                newBtn.innerHTML = '&#128065;&nbsp; Mostrar resposta';
                newBtn.style.cssText = `position:absolute;top:50%;left:50%;transform:translate(-50%,-50%);background:${color};color:white;border:none;border-radius:8px;padding:8px 18px;font-size:13px;font-weight:600;cursor:pointer;z-index:20;font-family:inherit;display:flex;align-items:center;gap:6px;white-space:nowrap;box-shadow:0 4px 16px rgba(0,0,0,0.25);`;
                callout.appendChild(newBtn);
            }

            // Mostrar/ocultar botão "Ocultar"
            callout.querySelectorAll<HTMLElement>('.gabarito-hide-btn').forEach(b => {
                b.style.display = isReveal ? 'inline-flex' : 'none';
            });
        };

        document.addEventListener('click', handleGabaritoClick, true);
        return () => document.removeEventListener('click', handleGabaritoClick, true);
    }, []);

    // Inicializar botões de gabarito ao trocar página / carregar conteúdo
    useEffect(() => {
        const initGabarito = () => {
            document.querySelectorAll<HTMLElement>('.resumo-callout[data-gabarito="true"]').forEach(el => {
                const color = el.dataset.calloutColor || getComputedStyle(document.body).getPropertyValue('--platform-accent').trim();
                const isRevealed = el.dataset.gabaritoRevealed === 'true';

                // Remover TODOS os filhos diretos que não são conteúdo (callout-title / callout-body)
                // Isso elimina botões salvos pelo editor (InlineContentManager) e botões antigos nossos
                Array.from(el.children).forEach(child => {
                    const tag = child.tagName.toLowerCase();
                    const hasContentClass = child.classList.contains('callout-title') || child.classList.contains('callout-body');
                    if (!hasContentClass && (tag === 'button' || tag === 'div')) {
                        child.remove();
                    }
                });

                el.querySelectorAll<HTMLElement>('.callout-title, .callout-body').forEach(ce => {
                    ce.style.filter = isRevealed ? '' : 'blur(7px)';
                    ce.style.userSelect = isRevealed ? '' : 'none';
                    ce.style.pointerEvents = isRevealed ? '' : 'none';
                });

                if (!isRevealed) {
                    const revealBtn = document.createElement('button');
                    revealBtn.className = 'gabarito-reveal-btn';
                    revealBtn.innerHTML = '&#128065;&nbsp; Mostrar resposta';
                    revealBtn.style.cssText = `position:absolute;top:50%;left:50%;transform:translate(-50%,-50%);background:${color};color:white;border:none;border-radius:8px;padding:8px 18px;font-size:13px;font-weight:600;cursor:pointer;z-index:20;font-family:inherit;display:flex;align-items:center;gap:6px;white-space:nowrap;box-shadow:0 4px 16px rgba(0,0,0,0.25);`;
                    el.appendChild(revealBtn);
                }

                const hideBtn = document.createElement('button');
                hideBtn.className = 'gabarito-hide-btn';
                hideBtn.textContent = 'Ocultar';
                hideBtn.style.cssText = `position:absolute;top:8px;right:8px;background:rgba(0,0,0,0.35);color:white;border:none;border-radius:6px;padding:4px 8px;font-size:11px;font-weight:600;cursor:pointer;font-family:inherit;display:${isRevealed ? 'inline-flex' : 'none'};z-index:10;`;
                el.appendChild(hideBtn);
            });
        };

        const timeout = setTimeout(initGabarito, 300);
        return () => clearTimeout(timeout);
    }, [currentPageIndex, loading, pages, mode]);

    useEffect(() => {
        if (mode === 'read') {
            document.body.classList.remove('highlight-mode');
            document.body.classList.remove('remove-mode');
        }
    }, [mode]);

    useEffect(() => {
        const handleImageClick = (e: MouseEvent) => {
            const target = e.target as HTMLElement;

            if (target.tagName === 'IMG' && target.closest('.rrp-content')) {
                e.preventDefault();
                e.stopPropagation();

                const imgSrc = (target as HTMLImageElement).src;
                setZoomedImage(imgSrc);
                setZoomScale(1);
            }
        };

        document.addEventListener('click', handleImageClick);

        return () => {
            document.removeEventListener('click', handleImageClick);
        };
    }, []);

    // ========== TROCAR PÁGINA ==========
    const handlePageChange = useCallback((index: number) => {
        if (!isPurchasedDeck && index >= visiblePagesCount) {
            setShowPageLimitModal(true);
            return;
        }
        setCurrentPageIndex(index);
    }, [isPurchasedDeck, visiblePagesCount]);

    // Handler de links internos (mesma apostila ou outra apostila)
    useEffect(() => {
        const handleInternalLink = (e: MouseEvent) => {
            const target = e.target as HTMLElement;
            const linkEl = target.closest('.resumo-internal-link') as HTMLElement | null;
            if (!linkEl) return;

            e.preventDefault();
            e.stopPropagation();

            const type = linkEl.dataset.linkType;

            if (type === 'page') {
                const pageId = linkEl.dataset.pageId;
                const pageIndex = pages.findIndex(p => p.id === pageId);
                if (pageIndex !== -1) {
                    handlePageChange(pageIndex);
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                }
            } else if (type === 'apostila') {
                const href = linkEl.dataset.href;
                if (href) window.open(href, '_blank', 'noopener,noreferrer');
            }
        };

        document.addEventListener('click', handleInternalLink);
        return () => document.removeEventListener('click', handleInternalLink);
    }, [pages, handlePageChange]);

    useEffect(() => {
        const timer = setTimeout(() => {
            const mediaContainers = document.querySelectorAll('.media-container');

            mediaContainers.forEach(container => {
                const el = container as HTMLElement;

                el.dataset.mode = 'preview';
                el.classList.remove('selected');
                el.querySelectorAll('.resize-handle').forEach(handle => handle.remove());

                // Usa data-media-type (estável), não querySelector('audio') que some após injeção
                if (el.dataset.mediaType === 'audio') {
                    el.style.pointerEvents = 'auto';
                } else {
                    el.style.pointerEvents = 'none';
                    const img = el.querySelector('img, video');
                    if (img) {
                        (img as HTMLElement).style.pointerEvents = 'auto';
                        (img as HTMLElement).style.cursor = 'pointer';
                    }
                }
            });
        }, 100);

        return () => clearTimeout(timer);
    }, [currentPageIndex, pages, loading]);

    // Substitui <audio> nativo por player DOM puro (sem React/createRoot)
    // para evitar conflito com o event delegation do React root principal.
    useEffect(() => {
        const timer = setTimeout(() => {
            const audioContainers = document.querySelectorAll<HTMLElement>('.media-container[data-media-type="audio"]');
            audioContainers.forEach(container => {
                if (container.dataset.audioInjected === 'true') return;

                const nativeAudio = container.querySelector('audio');
                if (!nativeAudio) return;
                const src = nativeAudio.getAttribute('src') || '';
                if (!src) return;

                const player = buildDOMaudioPlayer(src);
                nativeAudio.replaceWith(player);
                container.dataset.audioInjected = 'true';
            });
        }, 200);

        return () => clearTimeout(timer);
    }, [currentPageIndex, pages, loading]);

    useEffect(() => {
        const container = scrollRef.current;
        if (!container) return;

        const handleScroll = () => {
            const scrollTop = container.scrollTop;
            const scrollHeight = container.scrollHeight - container.clientHeight;
            const progress = scrollHeight > 0 ? (scrollTop / scrollHeight) * 100 : 0;
            setReadProgress(Math.min(100, Math.max(0, progress)));
        };

        container.addEventListener('scroll', handleScroll, { passive: true });
        return () => container.removeEventListener('scroll', handleScroll);
    }, []);

    useEffect(() => {
        if (mode !== 'annotate') return;

        const updateToolbarPosition = () => {
            const headerEl = headerRef.current;
            const toolbarEl = toolbarRef.current;

            if (!headerEl || !toolbarEl) return;

            const isHeaderVisible = headerEl.classList.contains('visible');
            const headerHeight = isHeaderVisible ? headerEl.offsetHeight : 0;

            toolbarEl.style.setProperty('--toolbar-top', `${headerHeight}px`);
        };

        window.addEventListener('scroll', updateToolbarPosition, { passive: true });

        const observer = new MutationObserver(updateToolbarPosition);
        if (headerRef.current) {
            observer.observe(headerRef.current, {
                attributes: true,
                attributeFilter: ['class']
            });
        }

        updateToolbarPosition();

        return () => {
            window.removeEventListener('scroll', updateToolbarPosition);
            observer.disconnect();
        };
    }, [mode]);

    // ✅ Permitir cliques em badges mesmo com toolbar ativa
    useEffect(() => {
        if (mode !== 'annotate') return;

        // Aumentar z-index dos badges para ficarem acima da toolbar
        const badges = document.querySelectorAll('[data-quiz-id]');
        badges.forEach(badge => {
            const badgeEl = badge as HTMLElement;
            badgeEl.style.position = 'relative';
            badgeEl.style.zIndex = '999';
            badgeEl.style.pointerEvents = 'auto';
        });

        return () => {
            // Limpar ao sair do modo anotação
            badges.forEach(badge => {
                const badgeEl = badge as HTMLElement;
                badgeEl.style.position = '';
                badgeEl.style.zIndex = '';
                badgeEl.style.pointerEvents = '';
            });
        };
    }, [mode, currentPageIndex, pages]);

    // ========== DESABILITAR BADGES (NÃO EDITÁVEIS) ==========
    useEffect(() => {
        if (resumo) {
            const badges = document.querySelectorAll('.flashcard-badge');
            badges.forEach(badge => {
                const badgeEl = badge as HTMLElement;
                badgeEl.removeAttribute('data-editable');
                badgeEl.style.cursor = 'default';
            });
        }
    }, [resumo, currentPageIndex]);

    // ========== BADGE TOOLTIP NO MODO LEITURA ==========
    useEffect(() => {
        const handleEyeClick = (e: MouseEvent) => {
            const target = e.target as HTMLElement;
            const eyeBtn = target.closest('.badge-eye-btn') as HTMLElement | null;
            if (!eyeBtn) return;

            e.stopPropagation();
            e.preventDefault();

            const badge = eyeBtn.closest('.flashcard-badge') as HTMLElement | null;
            if (!badge) return;

            const cardIndex = parseInt(badge.dataset.cardIndex || '0');
            // cards foi removido — buscar do DOM não é possível aqui
            // precisamos manter cards no state

            // verificar tooltip existente
            const existing = document.querySelector('.badge-inline-tooltip') as HTMLElement | null;
            if (existing) {
                const existingId = (existing as any)._badgeId;
                if (existingId === badge.dataset.cardId) {
                    const isShowingFront = existing.dataset.showing === 'front';
                    existing.dataset.showing = isShowingFront ? 'back' : 'front';
                    const label = existing.querySelector('.bit-label') as HTMLElement;
                    const text = existing.querySelector('.bit-text') as HTMLElement;
                    if (label) label.textContent = isShowingFront ? 'RESPOSTA' : 'PERGUNTA';
                    if (text) text.textContent = isShowingFront
                        ? (cardsRef.current[cardIndex]?.back || '')
                        : (cardsRef.current[cardIndex]?.front || '');
                    return;
                } else {
                    existing.remove();
                }
            }

            const card = cardsRef.current[cardIndex];
            if (!card) return;

            const tooltip = document.createElement('div');
            tooltip.className = 'badge-inline-tooltip';
            tooltip.dataset.showing = 'front';
            (tooltip as any)._badgeId = badge.dataset.cardId;
            tooltip.innerHTML = `<div class="bit-label">PERGUNTA</div><div class="bit-text">${card.front || ''}</div>`;

            const rect = badge.getBoundingClientRect();
            tooltip.style.cssText = `position:fixed; left:${rect.left}px; top:${rect.bottom + 6}px; z-index:100000; background:white; border:1px solid #e5e7eb; border-radius:10px; padding:12px 16px; width:280px; box-shadow:0 8px 24px rgba(0,0,0,0.12); font-size:13px; line-height:1.5;`;
            document.body.appendChild(tooltip);

            const tRect = tooltip.getBoundingClientRect();
            if (tRect.right > window.innerWidth) tooltip.style.left = `${window.innerWidth - tRect.width - 12}px`;

            const remove = () => { tooltip.remove(); badge.removeEventListener('mouseleave', remove); };
            badge.addEventListener('mouseleave', remove);
        };

        document.addEventListener('click', handleEyeClick);
        return () => document.removeEventListener('click', handleEyeClick);
    }, []);

    // ========== FULLSCREEN ==========
    const toggleFullScreen = async () => {
        if (!document.fullscreenElement) {
            await document.documentElement.requestFullscreen();
        } else {
            document.exitFullscreen();
        }
    };

    useEffect(() => {
        const handleFullscreenChange = () => {
            setIsFullscreen(!!document.fullscreenElement);
        };

        document.addEventListener('fullscreenchange', handleFullscreenChange);
        return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
    }, []);

    // ========== NAVEGAÇÃO ==========
    const goBack = useCallback(() => {
        // Sair do fullscreen de forma não-bloqueante
        if (document.fullscreenElement) {
            document.exitFullscreen().catch(() => {});
        }

        // Prévia pública: não existe "voltar pra lista de subdecks" (não há
        // deckId de rota nem sessão) — volta pra página pública do produto que
        // vende este conteúdo (mesmo destino de goToPreviewProduct, mesmo
        // checkoutSlug resolvido pelo backend), com fallback pro login só
        // quando não há produto público associado.
        if (isPreview) {
            // Aberta pelo selo "Prévia" da RyanFieldPage (aluno logado): volta pro campo
            const previewReturnPath = (location.state as any)?.returnPath;
            if (previewReturnPath) {
                navigate(previewReturnPath);
                return;
            }
            navigate(previewData?.checkoutSlug ? `/ryan/produto/${previewData.checkoutSlug}` : '/login');
            return;
        }

        // Detectar contexto Ryan
        const fromRyan = (location.state as any)?.fromRyan === true;
        const ryanReturnPath = (location.state as any)?.returnPath;

        const backPath = fromRyan && ryanReturnPath
            ? ryanReturnPath
            : isCustomDeck
                ? `/custom-decks/${deckId}/subdecks`
                : `/subdecks/${deckId}`;

        // Hard navigation garante que a página seja substituída independentemente de estado React
        const doNavigate = () => { window.location.href = backPath; };

        if (isDirtyRef.current) {
            setPendingAction(() => doNavigate);
            setShowUnsavedModal(true);
        } else {
            doNavigate();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isCustomDeck, deckId, isPreview, previewData, navigate]);

    const goToEditor = () => {
        const editorPath = isCustomDeck
            ? `/custom-decks/${deckId}/resumo/${subdeckId}/edit`
            : `/resumo/${deckId}/${subdeckId}/edit`;

        // Passar contexto Ryan para o editor — isRyanEditor libera o acesso
        // direto (sem modo revisor/aprovação) pro editor Ryan no destino
        const fromRyan = (location.state as any)?.fromRyan === true;
        const ryanReturnPath = (location.state as any)?.returnPath;

        navigate(editorPath, {
            state: fromRyan && ryanReturnPath
                ? { fromRyan: true, returnPath: ryanReturnPath, isRyanEditor }
                : undefined
        });
    };

    const goToRevisor = () => {
        const editorPath = isCustomDeck
            ? `/custom-decks/${deckId}/resumo/${subdeckId}/edit`
            : `/resumo/${deckId}/${subdeckId}/edit`;
        navigate(editorPath, { state: { mode: 'revisor' } });
    };

    // ========== LOADING ==========
    if (loading) {
        return (
            <div className={`rrp-page ${darkMode ? 'dark' : ''}`}>
                <div className="rrp-loading">Carregando apostila...</div>
            </div>
        );
    }

    // ========== EMPTY STATE ==========
    if (!resumo || pages.length === 0) {
        const goToEditorWithImport = () => {
            const editorPath = isCustomDeck
                ? `/custom-decks/${deckId}/resumo/${subdeckId}/edit`
                : `/resumo/${deckId}/${subdeckId}/edit`;
            navigate(editorPath, { state: { openImport: true } });
        };

        return (
            <div className={`rrp-page ${darkMode ? 'dark' : ''}`}>
                <div className="rrp-header">
                    <button onClick={goBack} className="rrp-btn-back">
                        <ArrowLeft size={20} />
                    </button>
                </div>
                <div className="rrp-empty">
                    <div className="rrp-empty-icon">
                        <BookOpen size={64} strokeWidth={1.2} />
                    </div>
                    <h2>Nenhuma apostila disponível</h2>
                    <p>Este subdeck ainda não possui uma apostila publicada.</p>
                    {isAdmin && !isPreview && (
                        <>
                            <button className="rrp-btn-create-apostila" onClick={goToEditor}>
                                <BookOpen size={18} />
                                Criar Apostila
                            </button>
                            <button className="rrp-btn-create-apostila rrp-btn-import-apostila" onClick={goToEditorWithImport}>
                                <Upload size={18} />
                                Importar Apostila
                            </button>
                        </>
                    )}
                </div>
            </div>
        );
    }

    const currentPage = pages[currentPageIndex];

    return (
        <div
            id="rrp-scroll-container"
            ref={scrollRef}
            className={`rrp-page ${darkMode ? 'dark' : ''} ${isFullscreen ? 'fullscreen' : ''} ${mode === 'annotate' ? 'rrp-annotate-mode' : ''}`}
            style={{
                overflow: 'auto',
                height: isFullscreen ? '100vh' : 'auto'
            }}
        >
            {/* Progress Bar */}
            <div className="rrp-progress-bar" style={{ width: `${readProgress}%` }} />

            {/* Header com Auto-Hide */}
            <AutoHideHeader threshold={80} darkMode={darkMode} ref={headerRef}>
                <div className="rrp-header rrp-header-compact">
                    {/* Esquerda: Botão Voltar (só ícone) */}
                    <button
                        className="rrp-btn-back rrp-btn-icon-only"
                        onClick={goBack}
                        title={isPreview ? 'Sair da prévia' : 'Voltar'}
                    >
                        <ChevronLeft size={20} />
                    </button>

                    {/* Centro: Navegação Compacta */}
                    <CompactPageNav
                        currentPage={currentPageIndex}
                        totalPages={pages.length}
                        onPageChange={handlePageChange}
                        darkMode={darkMode}
                    />

                    {/* Direita: Ações */}
                    <div className="rrp-header-actions">
                        {isPreview && <span className="rrp-preview-badge">Prévia</span>}

                        <button
                            className="rrp-btn-header rrp-btn-icon-only"
                            onClick={toggleFullScreen}
                            title={isFullscreen ? 'Sair de Tela Cheia' : 'Tela Cheia'}
                        >
                            {isFullscreen ? <Minimize2 size={18} /> : <Maximize2 size={18} />}
                        </button>

                        <button
                            className={`rrp-btn-header ${mode === 'annotate' ? 'active' : ''}`}
                            onClick={() => {
                                if (mode === 'read') {
                                    setMode('annotate');
                                    if (isPreview) {
                                        setToast({ message: 'Você pode ver a interface de anotações — grifar, comentar e anotar de verdade é só para assinantes.', type: 'info' });
                                    } else {
                                        onboarding.onInteractModeEntered();
                                    }
                                } else {
                                    setMode('read');
                                    if (!isPreview) onboarding.onInteractModeClosed();
                                }
                            }}
                            title={mode === 'read' ? 'Ativar Modo Interagir' : 'Voltar para Leitura'}
                        >
                            {mode === 'read' ? <Edit size={18} /> : <Eye size={18} />}
                            <span>{mode === 'read' ? 'Interagir' : 'Ler'}</span>
                        </button>

                        {isApostilaRevisor && !isAdmin && !isRyanEditor && !isPreview && (
                            <button
                                className="rrp-btn-header rrp-btn-revisor"
                                onClick={goToRevisor}
                                title="Revisar Apostila"
                            >
                                <Edit3 size={18} />
                                <span>Revisar</span>
                            </button>
                        )}

                        {isRyanEditor && !isAdmin && !isPreview && (
                            <button
                                className="rrp-btn-header rrp-btn-revisor"
                                onClick={goToEditor}
                                title="Editar Apostila"
                            >
                                <Edit3 size={18} />
                                <span>Editar</span>
                            </button>
                        )}

                        {isAdmin && !isPreview && (
                            <button
                                className="rrp-btn-header"
                                onClick={goToEditor}
                                title="Editar Resumo"
                            >
                                <Edit3 size={18} />
                                <span>Editar</span>
                            </button>
                        )}
                    </div>
                </div>
            </AutoHideHeader>

            {/* Faixa de prévia — contexto pra quem chegou por um link público */}
            {isPreview && previewData && (
                <div className="rrp-preview-banner">
                    <span>
                        Você está visualizando uma <strong>prévia</strong> de <em>{previewData.subdeckName}</em>
                        {previewData.deckName ? ` — ${previewData.deckName}` : ''}
                        {previewData.previewPagesLimit != null && (
                            <> &nbsp;·&nbsp; {Math.min(previewData.previewPagesLimit, pages.length)} de {pages.length} {pages.length === 1 ? 'página' : 'páginas'} liberadas</>
                        )}
                    </span>
                </div>
            )}

            {/* ✅ TOOLBAR DE ANOTAÇÕES (quando modo = 'annotate') */}
            {mode === 'annotate' && (
                <div ref={toolbarRef}>
                    {/* Prévia: a toolbar/sidebar/margem de comentários inteira fica
                        visualmente presente mas inerte a clique. Trocar só os
                        callbacks onAddHighlight, onAddComment etc. (abaixo) NÃO
                        bastava — useHighlight grifa o DOM diretamente ao ativar o
                        modo grifo e selecionar texto, antes mesmo de chamar
                        onAddHighlight; bloquear só o callback deixava o
                        grifo/comentário acontecer de verdade e só a persistência
                        falhar. pointer-events:none aqui impede que QUALQUER botão
                        de ativação (grifar, comentar, categorias de nota) chegue a
                        rodar, então a criação nunca começa — único jeito de
                        garantir que a ação "não faz nada de fato". */}
                    <div style={isPreview ? { pointerEvents: 'none' } : undefined}>
                        <AnnotationLayer
                            pageId={currentPage?.id || ''}
                            annotations={annotations}
                            onAddHighlight={isPreview ? handlePreviewAnnotationBlocked : addHighlight}
                            onRemoveHighlight={isPreview ? handlePreviewAnnotationBlocked : removeHighlight}
                            onAddNote={isPreview ? handlePreviewAnnotationBlocked : addNote}
                            onUpdateNote={isPreview ? handlePreviewAnnotationBlocked : updateNoteComplete}
                            onUpdateHighlightNote={isPreview ? handlePreviewAnnotationBlocked : updateHighlightNote}
                            onRemoveNote={isPreview ? handlePreviewAnnotationBlocked : removeNote}
                            onAddComment={isPreview ? handlePreviewAnnotationBlocked : addComment}
                            onRemoveComment={isPreview ? handlePreviewAnnotationBlocked : removeComment}
                            onUpdateComment={isPreview ? handlePreviewAnnotationBlocked : updateComment}
                            onDirtyChange={setIsDirty}
                            onSave={isPreview ? handlePreviewSave : saveAnnotations}
                            onClose={() => setMode('read')}
                            darkMode={darkMode}
                            onFirstHighlightMade={onboarding.onFirstHighlightMade}
                            onFirstNoteMade={onboarding.onFirstNoteMade}
                            contentRef={contentAreaRef}
                            authorName={user?.email?.split('@')[0] || 'Eu'}
                            onUndo={annotationUndo}
                            onRedo={annotationRedo}
                            canUndo={annotationCanUndo}
                            canRedo={annotationCanRedo}
                        />
                    </div>
                </div>
            )}

            {/* Content */}
            <div
                className="rrp-content"
                ref={(el) => { contentAreaRef.current = el; }}
                style={{
                    position: 'relative',
                    userSelect: mode === 'read' && !isAdmin ? 'none' : 'auto',
                    paddingTop: mode === 'annotate' ? '120px' : '40px',
                    marginRight: mode === 'annotate' && (annotations.comments || []).filter(c => c.pageId === currentPage?.id).length > 0 ? '280px' : undefined,
                }}
            >
                {splitContentByQuiz(currentPage?.content || '<p>Sem conteúdo</p>').map((segment, index) => {
                    if (segment.type === 'quiz' && segment.quizId) {
                        const data = quizReadManager.getQuizData(segment.quizId);
                        if (!data || data.questions.length === 0) return null;
                        return (
                            <QuizInlineAccordion
                                key={segment.quizId}
                                quizId={segment.quizId}
                                quizData={data}
                                darkMode={darkMode}
                                progress={getQuizProgress(segment.quizId)}
                                onProgressChange={handleQuizProgressChange}
                                locked={isPreview}
                                onLockedCtaClick={isPreview ? goToPreviewCheckout : undefined}
                            />
                        );
                    }

                    return (
                        <HighlightedContent
                            key={`html-${index}`}
                            html={segment.html || ''}
                            highlights={mode === 'annotate' ? annotations.highlights : []}
                            pageId={currentPage?.id || ''}
                            darkMode={darkMode}
                            onReconciled={(pid, reconciled) => {
                                bulkUpdateHighlights(pid, reconciled);
                            }}
                        />
                    );
                })}
            </div>

            {/* Footer com Auto-Hide (navegação ao final) */}
            {pages.length > 1 && (
                <AutoHideFooter
                    threshold={300}
                    darkMode={darkMode}
                >
                    <PageNavigator
                        pages={pages}
                        currentPageIndex={currentPageIndex}
                        onPageChange={handlePageChange}
                        onAddPage={() => { }}
                        isEditing={false}
                        darkMode={darkMode}
                        maxVisibleIndex={isPurchasedDeck ? pages.length - 1 : visiblePagesCount - 1}
                    />
                </AutoHideFooter>
            )}

            {/* ✅ NOVO: ADICIONAR ANTES DO ÚLTIMO </div> */}
            {zoomedImage && (
                <ImageZoomModal
                    imageUrl={zoomedImage}
                    onClose={() => {
                        setZoomedImage(null);
                        setZoomScale(1);
                    }}
                    zoomScale={zoomScale}
                    onZoomChange={setZoomScale}
                />
            )}

            {/* Toast */}
            {toast && (
                <Toast
                    message={toast.message}
                    type={toast.type}
                    onClose={() => setToast(null)}
                />
            )}

            {/* ✅ MODAL DE ALTERAÇÕES NÃO SALVAS (GLOBAL DA PÁGINA) */}
            <UnsavedChangesModal
                isOpen={showUnsavedModal}
                onSave={async () => {
                    const result = await saveAnnotations();

                    if (result.success) {
                        setIsDirty(false);
                        setShowUnsavedModal(false);
                        if (pendingAction) {
                            pendingAction();
                            setPendingAction(null);
                        }
                    }
                }}
                onDiscard={() => {
                    setIsDirty(false);
                    setShowUnsavedModal(false);
                    if (pendingAction) {
                        pendingAction();
                        setPendingAction(null);
                    }
                }}
                onCancel={() => {
                    setShowUnsavedModal(false);
                    setPendingAction(null);
                }}
                darkMode={darkMode}
            />

            {/* Tutorial é recurso de quem já tem conta — nunca aparece na prévia
                (nem chamamos onInteractModeEntered/onFirstHighlightMade/etc acima
                em modo prévia, então o localStorage do onboarding também não é
                tocado por uma sessão anônima). */}
            {!isPreview && (
                <ResumoOnboarding
                    currentStep={onboarding.currentStep}
                    isVisible={onboarding.isVisible}
                    onDismissStep={onboarding.dismissStep}
                    highlightCommentPosition={onboarding.highlightCommentPosition}
                    anchorPosition={onboarding.anchorPosition}
                />
            )}

            {isPreview ? (
                // Fim da prévia = mesmo modal "Quase lá" da RyanFieldPage: botão pro
                // link de compra configurado no campo (ou página do produto) + WhatsApp
                <RyanCompraModal
                    isOpen={showPageLimitModal}
                    onClose={() => setShowPageLimitModal(false)}
                    spaceName={previewData?.productTitle || previewData?.deckName || ''}
                    preset={{
                        purchaseUrl: previewData?.purchaseUrl || '',
                        productTitle: previewData?.productTitle || '',
                        publicSlug: previewData?.checkoutSlug || null,
                        whatsappNumber: previewData?.whatsappNumber || '',
                    }}
                    title="Prévia encerrada"
                    message={
                        <>Você visualizou as <strong>{visiblePagesCount}</strong> páginas disponíveis nesta prévia.
                            O restante faz parte de <strong>"{previewData?.productTitle || previewData?.deckName}"</strong>.</>
                    }
                />
            ) : (
                <PageLimitModal
                    isOpen={showPageLimitModal}
                    visiblePagesCount={visiblePagesCount}
                    darkMode={darkMode}
                    onClose={() => setShowPageLimitModal(false)}
                />
            )}

            {/* ====== MODAL DE RECUPERAÇÃO DE AUTOSAVE ====== */}
            {showAutosaveRecovery && (
                <div style={{
                    position: 'fixed', inset: 0, zIndex: 99999,
                    background: 'rgba(0,0,0,0.55)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    padding: '20px',
                    backdropFilter: 'blur(4px)',
                }}>
                    <div style={{
                        background: darkMode ? '#0f1b10' : 'white',
                        borderRadius: '20px',
                        padding: '32px',
                        maxWidth: '400px',
                        width: '100%',
                        boxShadow: '0 24px 64px rgba(0,0,0,0.35)',
                        border: darkMode ? '1px solid rgba(95,186,97,0.2)' : '1px solid #e5e7eb',
                    }}>
                        {/* Ícone */}
                        <div style={{
                            width: 52, height: 52,
                            background: 'var(--platform-a10)',
                            borderRadius: '14px',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            marginBottom: '20px',
                        }}>
                            <RefreshCw size={24} color="var(--platform-primary)" />
                        </div>
                        <h2 style={{
                            margin: '0 0 8px',
                            fontSize: '18px',
                            fontWeight: 700,
                            color: darkMode ? '#f5f7f5' : '#111827',
                            fontFamily: 'Inter, sans-serif',
                        }}>Progresso recuperado</h2>
                        <p style={{
                            margin: '0 0 24px',
                            fontSize: '14px',
                            color: darkMode ? '#9ca3af' : '#6b7280',
                            lineHeight: 1.65,
                            fontFamily: 'Inter, sans-serif',
                        }}>
                            Encontramos anotações salvas automaticamente às{' '}
                            <strong style={{ color: darkMode ? 'var(--platform-accent-light)' : 'var(--platform-accent)' }}>{autosaveRecoveryTime}</strong>.
                            Deseja restaurar esse progresso?
                        </p>
                        <div style={{ display: 'flex', gap: '10px' }}>
                            <button
                                onClick={() => {
                                    const info = getAutosaveInfo();
                                    if (info) {
                                        import('../services/annotation.service').then(({ annotationService }) => {
                                            annotationService.save(info.data).then(() => {
                                                reloadAnnotations();
                                                clearAutosave();
                                            });
                                        });
                                    }
                                    setShowAutosaveRecovery(false);
                                }}
                                style={{
                                    flex: 1, padding: '11px',
                                    background: 'var(--platform-accent)', color: 'white',
                                    border: 'none', borderRadius: '10px',
                                    fontSize: '14px', fontWeight: 600,
                                    cursor: 'pointer', fontFamily: 'Inter, sans-serif',
                                    transition: 'background 0.15s',
                                }}
                            >
                                Restaurar
                            </button>
                            <button
                                onClick={() => { clearAutosave(); setShowAutosaveRecovery(false); }}
                                style={{
                                    flex: 1, padding: '11px',
                                    background: darkMode ? '#1a2e1a' : '#f3f4f6',
                                    color: darkMode ? '#d1d5db' : '#374151',
                                    border: darkMode ? '1px solid rgba(95,186,97,0.15)' : '1px solid #e5e7eb',
                                    borderRadius: '10px',
                                    fontSize: '14px', fontWeight: 600,
                                    cursor: 'pointer', fontFamily: 'Inter, sans-serif',
                                }}
                            >
                                Descartar
                            </button>
                        </div>
                    </div>
                </div>
            )}

        </div>
    );
};

export default ResumoReadPage;